import './AnnotationView.scss';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { ReactElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { HiddenAnnotationsToastContainer } from '../../../containers/HiddenAnnotationsToast.container';
import { ProjectRole } from '../../../models/userRole.model';
import { AnnotationMoreOptions } from '../../../modules/annotation/components/AnnotationMoreOptions';
import { AnnotationTopBarItem } from '../../../modules/annotation/components/AnnotationTopBarItem';
import { AnnotationViewOptions } from '../../../modules/annotation/components/AnnotationViewOptions';
import { Image } from '../../../modules/annotation/components/Image';
import { NoImagePlaceholder } from '../../../modules/annotation/components/NoImagePlaceholder';
import { AnnotationImageNameHeaderContainer } from '../../../modules/annotation/containers/AnnotationImageNameHeader.container';
import { AnnotationStatusInfoContainer } from '../../../modules/annotation/containers/AnnotationStatusInfoContainer';
import { BackToProjectsButtonContainer } from '../../../modules/annotation/containers/BackToProjectsButtonContainer';
import { CorrectControlsContainer } from '../../../modules/annotation/containers/CorrectControls.container';
import { DrawingContainer } from '../../../modules/annotation/containers/Drawing.container';
import { ImageContainer } from '../../../modules/annotation/containers/Image.container';
import { QuestionsContainer } from '../../../modules/annotation/containers/Questions.container';
import { RedirectFromAnnotationContainer } from '../../../modules/annotation/containers/RedirectFromAnnotationContainer';
import { ReturnToToolsContainer } from '../../../modules/annotation/containers/ReturnToTools.container';
import { ReviewControlsContainer } from '../../../modules/annotation/containers/ReviewControls.container';
import { ReviewProgressContainer } from '../../../modules/annotation/containers/ReviewProgress.container';
import { SegmentationsContainer } from '../../../modules/annotation/containers/Segmentations.container';
import { SegmentationsSaveContainer } from '../../../modules/annotation/containers/SegmentationsSave.container';
import { UndoRedoAnnotationsShortcutsContainer } from '../../../modules/annotation/containers/UndoRedoAnnotationsShortcuts.container';
import { ViewAttributesContainer } from '../../../modules/annotation/containers/ViewAttributesContainer';
import { AnnotationTypesContainer } from '../../../modules/annotation/submodules/annotationTypes/containers/AnnotationTypes.container';
import { ReviewContainer } from '../../../modules/annotation/submodules/review/containers/Review.container';
import { ReviewModeBuilderContainer } from '../../../modules/annotation/submodules/review/containers/ReviewModeBuilder.container';
import { ReviewModeSwitchContainer } from '../../../modules/annotation/submodules/review/containers/ReviewModeSwitch.container';
import { ReviewMode } from '../../../modules/annotation/submodules/review/models/reviewMode';
import { LeftMenuContainer } from '../../../modules/annotation/submodules/segmentations/containers/LeftMenu.container';
import { ImageSegmentationsContextProvider } from '../../../modules/annotation/submodules/segmentations/segmentationContextMenu/imageSegmentations.context';
import { UnlockWorkspaceModal } from '../../../modules/workspaces/components/UnlockWorkspaceModal';
import { CurrentWorkspaceContainer } from '../../../modules/workspaces/containers/CurrentWorkspace.container';
import { Home } from '../../../routes/config/Home';

export const AnnotationReviewView = (): ReactElement => {
  const { t } = useTranslator();
  const history = useHistory();

  const { workspaceId, projectId } = useParams<{ projectId: string; workspaceId: string }>();

  const backToProjectReview = (): ReactElement => (
    <Button
      color='neutral'
      variant='topbar'
      tabIndex={-1}
      onClick={() => {
        history.push(Home.Projects.List.All.withParams({ workspaceId: workspaceId }));
      }}
      startDecorator={<FontAwesomeIcon icon={faArrowLeft} />}
    >
      {t('back')}
    </Button>
  );

  const backToProjectCorrect = (): ReactElement => <BackToProjectsButtonContainer />;

  const moreOptionsReview = (): ReactElement => (
    <AnnotationMoreOptions
      review
      globalShortcuts={[]}
    />
  );

  const moreOptionsCorrect = (): ReactElement => (
    <AnnotationMoreOptions
      correct
      globalShortcuts={[]}
    />
  );

  const segmentationReview = (): ReactElement => <SegmentationsContainer readonly={true} />;

  const segmentationCorrect = (): ReactElement => (
    <>
      <SegmentationsContainer />
      <DrawingContainer disableCursorGuides={false} />
    </>
  );

  const undoRedoReview = (): ReactElement => <></>;

  const undoRedoCorrect = (): ReactElement => (
    <>
      <AnnotationTopBarItem>
        <UndoRedoAnnotationsShortcutsContainer undo={true} />
      </AnnotationTopBarItem>
      <AnnotationTopBarItem>
        <UndoRedoAnnotationsShortcutsContainer undo={false} />
      </AnnotationTopBarItem>
    </>
  );

  const controlsContainerReview = (): ReactElement => <ReviewControlsContainer />;

  const controlsContainerCorrect = (): ReactElement => <CorrectControlsContainer />;

  return (
    <div className='annotation-view'>
      <CurrentWorkspaceContainer>{(unlockModalProps): ReactElement => <UnlockWorkspaceModal {...unlockModalProps} />}</CurrentWorkspaceContainer>
      <RedirectFromAnnotationContainer allowedRoles={[ProjectRole.Reviewer]} />
      <ReviewContainer>
        {(reviewProps): ReactElement => (
          <ImageSegmentationsContextProvider imageId={reviewProps.image?.id}>
            <div className='annotation-view-top-bar'>
              <AnnotationTopBarItem>
                <ReviewModeSwitchContainer
                  reviewBuilder={backToProjectReview}
                  correctBuilder={backToProjectCorrect}
                />
              </AnnotationTopBarItem>
              <ReviewModeSwitchContainer
                reviewBuilder={undoRedoReview}
                correctBuilder={undoRedoCorrect}
              />
              <AnnotationTopBarItem>
                <AnnotationViewOptions disableCursorConfig={reviewProps.mode === ReviewMode.Review} />
              </AnnotationTopBarItem>
              <AnnotationTopBarItem>
                <ViewAttributesContainer />
              </AnnotationTopBarItem>
              <AnnotationTopBarItem variation='image-name'>
                <AnnotationImageNameHeaderContainer />
                <ReviewProgressContainer />
              </AnnotationTopBarItem>
              <AnnotationTopBarItem>
                <ReviewModeSwitchContainer
                  reviewBuilder={moreOptionsReview}
                  correctBuilder={moreOptionsCorrect}
                />
              </AnnotationTopBarItem>
            </div>
            <div className='annotation-view-inner'>
              <LeftMenuContainer
                currentProjectId={projectId}
                isReadOnly={true}
                showObjectsList={true}
                isReview={true}
              />
              <HiddenAnnotationsToastContainer />
              <ImageContainer>
                {(imageProps): ReactElement => (
                  <>
                    {reviewProps.noImage ? (
                      <NoImagePlaceholder
                        mode='Review'
                        canReview={true}
                        onRefresh={reviewProps.handleRefresh}
                        workspaceId={workspaceId}
                        noImageReason={reviewProps.noImageReason}
                      />
                    ) : (
                      <Image {...imageProps}>
                        <ReviewModeSwitchContainer
                          reviewBuilder={segmentationReview}
                          correctBuilder={segmentationCorrect}
                        />
                      </Image>
                    )}
                  </>
                )}
              </ImageContainer>
              <div className='right-column'>
                <div className='scrollable-container'>
                  <div className='scrollable-container-content'>
                    <ReviewModeBuilderContainer>
                      {(mode: ReviewMode): ReactElement => {
                        const readonly = mode === ReviewMode.Review;
                        return (
                          <>
                            <AnnotationTypesContainer readonly={readonly}>
                              <AnnotationStatusInfoContainer canChangeImageSet />
                              <ReturnToToolsContainer readonly={readonly} />
                            </AnnotationTypesContainer>
                            <QuestionsContainer readonly={readonly} />
                            {!readonly && <SegmentationsSaveContainer />}
                          </>
                        );
                      }}
                    </ReviewModeBuilderContainer>
                  </div>
                </div>
                <ReviewModeSwitchContainer
                  reviewBuilder={controlsContainerReview}
                  correctBuilder={controlsContainerCorrect}
                />
              </div>
            </div>
          </ImageSegmentationsContextProvider>
        )}
      </ReviewContainer>
    </div>
  );
};
